import type { Locale } from "@/lib/i18n";

type Answer = { axis: string; value: number };
type Progress = { answers: Record<string, Answer>; step: number };
type ProgressTest = {
  id: string;
  questions: Array<{ id: string; options: Array<Answer> }>;
};
type ProgressStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
type ProgressStorageIndex = Pick<Storage, "key" | "length" | "removeItem">;
type BrowserStorageHost = { readonly localStorage: Storage };
const STORAGE_PREFIX = "modutest:test-progress:v1";

export function getTestProgressStorage(
  host: BrowserStorageHost | undefined = typeof window === "undefined"
    ? undefined
    : window,
): Storage | null {
  try {
    return host?.localStorage ?? null;
  } catch {
    return null;
  }
}

export function testProgressKey(testId: string, locale: Locale): string {
  return `${STORAGE_PREFIX}:${locale}:${testId}`;
}

export function loadTestProgress(
  storage: ProgressStorage | null,
  test: ProgressTest,
  locale: Locale,
): Progress | null {
  if (!storage) return null;
  const key = testProgressKey(test.id, locale);
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      v?: unknown;
      questionIds?: unknown;
      answers?: unknown;
      step?: unknown;
    };
    const questionIds = test.questions.map((question) => question.id);
    if (
      parsed.v !== 1 ||
      !Array.isArray(parsed.questionIds) ||
      parsed.questionIds.length !== questionIds.length ||
      !parsed.questionIds.every((id, index) => id === questionIds[index]) ||
      !Number.isInteger(parsed.step) ||
      (parsed.step as number) < 0 ||
      (parsed.step as number) >= test.questions.length ||
      !parsed.answers ||
      typeof parsed.answers !== "object" ||
      Array.isArray(parsed.answers)
    ) {
      storage.removeItem(key);
      return null;
    }

    const questions = new Map(test.questions.map((question) => [question.id, question]));
    const entries = Object.entries(parsed.answers as Record<string, unknown>);
    if (entries.length === 0) {
      storage.removeItem(key);
      return null;
    }
    const answers: Record<string, Answer> = {};
    for (const [questionId, value] of entries) {
      const question = questions.get(questionId);
      if (!question || !value || typeof value !== "object" || Array.isArray(value)) {
        storage.removeItem(key);
        return null;
      }
      const answer = value as Partial<Answer>;
      if (!question.options.some((option) => option.axis === answer.axis && option.value === answer.value)) {
        storage.removeItem(key);
        return null;
      }
      answers[questionId] = { axis: answer.axis, value: answer.value } as Answer;
    }
    const firstMissing = test.questions.findIndex(
      (question) => !(question.id in answers),
    );
    const answeredPrefixLength =
      firstMissing === -1 ? test.questions.length : firstMissing;
    if (
      entries.length !== answeredPrefixLength ||
      answeredPrefixLength < (parsed.step as number)
    ) {
      storage.removeItem(key);
      return null;
    }
    return { answers, step: parsed.step as number };
  } catch {
    try {
      storage.removeItem(key);
    } catch {}
    return null;
  }
}

export function saveTestProgress(
  storage: ProgressStorage | null,
  test: ProgressTest,
  locale: Locale,
  progress: Progress,
): boolean {
  if (!storage) return false;
  try {
    storage.setItem(testProgressKey(test.id, locale), JSON.stringify({
      v: 1,
      questionIds: test.questions.map((question) => question.id),
      answers: progress.answers,
      step: progress.step,
    }));
    return true;
  } catch {
    return false;
  }
}

export function clearTestProgress(
  storage: ProgressStorage | null,
  testId: string,
  locale: Locale,
): boolean {
  if (!storage) return false;
  const key = testProgressKey(testId, locale);
  let tombstoned = false;
  try {
    storage.setItem(key, JSON.stringify({ v: 1, completed: true }));
    tombstoned = true;
  } catch {}
  try {
    storage.removeItem(key);
    return true;
  } catch {
    return tombstoned;
  }
}

export function clearAllTestProgress(
  storage: ProgressStorageIndex | null,
): boolean {
  if (!storage) return false;
  try {
    const keys = Array.from({ length: storage.length }, (_, index) =>
      storage.key(index),
    ).filter((key): key is string => key?.startsWith(`${STORAGE_PREFIX}:`) === true);
    keys.forEach((key) => storage.removeItem(key));
    return true;
  } catch {
    return false;
  }
}
